import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import ChartFrame from "@/components/ChartFrame";
import { TrendingUp, Activity, ListChecks, BookOpenText, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { CHART_MARGIN, TOOLTIP_STYLE, TICK_AXIS, LEGEND_STYLE, DOT_SM, INIT_DIM_LG } from "@/lib/chartStyles";

const T = { greenDeep: "#7FB069", slate: "#44546A" };
const DOMAIN_0_100 = [0, 100];
const TRACK_PALETTE = ["#6C8EBF", "#5d8a73", "#b07d3a", "#9A7AA0", "#C97064", "#4A5D6B", "#3f7a5c"];
const TEST_FIELDS = [
  ["standard_score", "Standard score"], ["sten", "STen"],
  ["percentile", "Percentile"], ["raw_score", "Raw score"],
];

const numOr = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };
const comparable = (t) => {
  for (const k of ["standard_score", "sten", "percentile", "raw_score"]) {
    const n = numOr(t[k]);
    if (n !== null) return n;
  }
  const subs = Object.values(t.subscores || {}).map(numOr).filter((n) => n !== null);
  return subs.length ? subs.reduce((a, b) => a + b, 0) : null;
};

function TrendArrow({ curr, prev }) {
  if (curr === null || prev === null) return null;
  if (curr > prev) return <span data-testid="test-trend-up" title={`Up from ${prev}`} className="inline-flex items-center gap-0.5 text-[11px] font-bold rounded-full px-1.5 py-0.5" style={{ color: "#3f7a5c", backgroundColor: "#DCEBE4" }}><ArrowUp size={11} /> Improving</span>;
  if (curr < prev) return <span data-testid="test-trend-down" title={`Down from ${prev}`} className="inline-flex items-center gap-0.5 text-[11px] font-bold rounded-full px-1.5 py-0.5" style={{ color: "#b04a4a", backgroundColor: "#FBE3E3" }}><ArrowDown size={11} /> Lower</span>;
  return <span data-testid="test-trend-same" title="No change" className="inline-flex items-center gap-0.5 text-[11px] font-bold rounded-full px-1.5 py-0.5" style={{ color: "#64748b", backgroundColor: "#EEF2F6" }}><Minus size={11} /> Steady</span>;
}

export function TestScores({ tests, reading }) {
  const byName = {};
  (tests || []).forEach((t) => { (byName[t.test_name] = byName[t.test_name] || []).push(t); });
  const groups = Object.values(byName);
  const lastRead = (reading || []).length ? reading[reading.length - 1] : null;
  const prevRead = (reading || []).length > 1 ? reading[reading.length - 2] : null;
  if (groups.length === 0 && !lastRead) return null;

  return (
    <section className="mt-8" data-testid="report-test-scores">
      <h2 className="font-['Outfit'] text-xl font-bold border-b-2 pb-2 flex items-center gap-2" style={{ color: T.slate, borderColor: "#6C8EBF" }}>
        <Activity size={20} style={{ color: T.greenDeep }} /> Standardised &amp; reading test scores
      </h2>
      <div className="space-y-3 mt-3">
        {groups.map((arr) => {
          const t = arr[0];
          const prev = arr[1];
          return (
            <div key={t.id} data-testid={`report-test-${t.id}`} className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                <span className="font-semibold font-['Outfit'] text-sm flex items-center gap-2" style={{ color: T.slate }}>
                  {t.test_name}
                  {prev && <TrendArrow curr={comparable(t)} prev={comparable(prev)} />}
                </span>
                <span className="text-xs text-slate-400">{t.date ? new Date(t.date).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {t.window && <span className="text-[11px] font-bold uppercase tracking-wide text-[#6C8EBF] bg-[#6C8EBF]/10 rounded-full px-2 py-0.5">{t.window}</span>}
                {t.risk_band && <span className="text-[11px] font-bold uppercase tracking-wide text-[#b07d3a] bg-[#b07d3a]/10 rounded-full px-2 py-0.5">{t.risk_band}</span>}
                {TEST_FIELDS.map(([k, label]) => t[k] ? <span key={k} className="text-slate-600"><span className="text-slate-400">{label}:</span> <b>{t[k]}</b></span> : null)}
                {t.subscores && Object.entries(t.subscores).filter(([, v]) => v !== "" && v != null).map(([k, v]) => (
                  <span key={k} className="text-slate-600"><span className="text-slate-400">{k}:</span> <b>{v}</b></span>
                ))}
              </div>
            </div>
          );
        })}
        {lastRead && (
          <div data-testid="report-reading-fluency" className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
              <span className="font-semibold font-['Outfit'] text-sm flex items-center gap-2" style={{ color: T.slate }}>
                <BookOpenText size={15} style={{ color: T.greenDeep }} /> Reading fluency
                {prevRead && <TrendArrow curr={numOr(lastRead.wcpm)} prev={numOr(prevRead.wcpm)} />}
              </span>
              <span className="text-xs text-slate-400">{lastRead.date ? new Date(lastRead.date).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <span className="text-slate-600"><span className="text-slate-400">Words correct / min:</span> <b>{lastRead.wcpm}</b></span>
              <span className="text-slate-600"><span className="text-slate-400">Accuracy:</span> <b>{lastRead.accuracy}%</b></span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function RunningLatest({ records }) {
  if (!records || records.length === 0) return null;
  const sorted = [...records].sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
  const latest = {};
  sorted.forEach((r) => { latest[r.template_id] = r; });
  const rows = Object.values(latest).sort((a, b) => a.template_name.localeCompare(b.template_name));
  if (rows.length === 0) return null;

  return (
    <section className="mt-8" data-testid="report-running-latest">
      <h2 className="font-['Outfit'] text-xl font-bold border-b-2 pb-2 flex items-center gap-2" style={{ color: T.slate, borderColor: "#5d8a73" }}>
        <ListChecks size={20} style={{ color: T.greenDeep }} /> Skills checklists — latest
      </h2>
      <div className="space-y-2 mt-3">
        {rows.map((r) => {
          const pct = r.total ? Math.round((r.score / r.total) * 100) : 0;
          return (
            <div key={r.template_id} data-testid={`report-running-${r.template_id}`} className="rounded-xl border border-slate-200 px-4 py-3">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="font-semibold text-slate-700">{r.template_name}</span>
                <span className="text-slate-500">{r.score}/{r.total} · {pct}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: T.greenDeep }} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function ProgressTracker({ records }) {
  if (!records || records.length === 0) return null;
  const sorted = [...records].sort((a, b) => (a.date > b.date) - (a.date < b.date));
  const names = [...new Set(sorted.map((r) => r.template_name))];
  const byDate = {};
  const order = [];
  sorted.forEach((r) => {
    const d = new Date(r.date).toLocaleDateString("en-IE", { day: "numeric", month: "short" });
    if (!byDate[d]) { byDate[d] = { date: d }; order.push(d); }
    byDate[d][r.template_name] = r.total ? Math.round((r.score / r.total) * 100) : 0;
  });
  const data = order.map((d) => byDate[d]);

  return (
    <section className="mt-8" data-testid="report-progress-tracker">
      <h2 className="font-['Outfit'] text-xl font-bold border-b-2 pb-2 flex items-center gap-2" style={{ color: T.slate, borderColor: "#b07d3a" }}>
        <TrendingUp size={20} style={{ color: T.greenDeep }} /> Progress over the term
      </h2>
      <p className="text-sm text-slate-400 mt-2 mb-4">Percentage known over time across each skill list.</p>
      <ChartFrame className="h-64 -ml-2">
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={240} initialDimension={INIT_DIM_LG}>
          <LineChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <XAxis dataKey="date" tick={TICK_AXIS} axisLine={false} tickLine={false} />
            <YAxis domain={DOMAIN_0_100} tick={TICK_AXIS} axisLine={false} tickLine={false} width={30} unit="%" />
            <Legend wrapperStyle={LEGEND_STYLE} />
            {names.map((n, i) => (
              <Line key={n} type="monotone" dataKey={n} stroke={TRACK_PALETTE[i % TRACK_PALETTE.length]}
                strokeWidth={2.5} dot={DOT_SM} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartFrame>
    </section>
  );
}
